import { Middleware } from 'redux';
import { configureStore, ThunkAction, Action, combineReducers } from '@reduxjs/toolkit';
import AtlasSdk from '@atlas/sdk';
import {
  persistReducer,
  persistStore,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  PersistorOptions,
  PersistConfig,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import sessionStorage from 'redux-persist/lib/storage/session';
// import logger from 'redux-logger';

import reducer from '@/slices';
import { CaseMultiTabsState } from '@/slices/case-multi-tabs';
import appReducer, { AppState } from '@/slices/app';
import caseMultiTabsReducer from '@/slices/case-multi-tabs';
import caseFieldReducer, { CaseFieldState } from '@/slices/case-field';
import caseFormReducer, { CaseFormState } from '@/slices/case-form';
import caseViewReducer, { CaseViewState } from '@/slices/case-view';
import tagsReducer, { TagsState } from '@/slices/tags';
import ebaReducer, { EbaState } from '@/slices/event-base-automation';
import ebaGroupReducer, { EbaGroupStateType } from '@/slices/event-base-automation-group';

interface PersistConfigType<S, RS = any, HSS = any, ESS = any> extends PersistConfig<S, RS, HSS, ESS> {
  whitelist: Array<keyof S extends string ? keyof S : string>;
  blacklist?: Array<keyof S extends string ? keyof S : string>;
}

const MIDDLEWARE_LIST: Middleware[] = [];

if (process.env.NODE_ENV !== 'production') {
  // MIDDLEWARE_LIST.push(logger);
}

const SetTransform = createTransform<CaseMultiTabsState, CaseMultiTabsState, CaseMultiTabsState, CaseMultiTabsState>(
  // transform state on its way to being serialized and persisted.
  (inboundState: CaseMultiTabsState, key) => {
    // convert mySet to an Array.
    const { sortIds, internalDB, activeId } = inboundState;
    const { entities } = internalDB;
    const new_sortIds = sortIds.filter((item: string) => /^\d+$/g.test(item));
    const new_ids = new_sortIds;
    const new_entities: any = {};
    let new_activeId = activeId;
    new_sortIds.forEach((item: string) => {
      new_entities[item] = entities[item];
    });
    if (activeId && !/^\d+$/g.test(activeId)) {
      new_activeId = new_sortIds[0] || '';
    }
    const new_inboundState = {
      sortIds: new_sortIds,
      internalDB: {
        ids: new_ids,
        entities: new_entities,
      },
      activeId: new_activeId,
      viewList: [],
    };
    return { ...inboundState, ...new_inboundState };
  },
  // transform state being rehydrated
  (outboundState, key) => {
    // convert mySet back to a Set.
    return { ...outboundState };
  },
  // define which reducers this transform gets called for.
  { whitelist: ['caseMultiTabs'] },
);

const appPersistConfig: PersistConfigType<AppState> = {
  key: 'app',
  version: 1,
  storage,
  whitelist: ['hiddenLicenseNotification', 'activeUserMapper'],
};

const caseMultiTabsPersistConfig: PersistConfigType<CaseMultiTabsState> = {
  key: 'case_multi_tabs',
  version: 1,
  storage,
  transforms: [SetTransform],
  whitelist: [
    'panelVisible',
    'viewList',
    'activedViewId',
    'hasViewListInterfaceError',
    'internalDB',
    'activeId',
    'sortIds',
  ],
};

const caseFieldPersistConfig: PersistConfigType<CaseFieldState> = {
  key: 'case_field',
  version: 1,
  storage,
  whitelist: ['checkedColumnKeys', 'sortedColumnKeys'],
};

const caseFormPersistConfig: PersistConfigType<CaseFormState> = {
  key: 'case_form',
  version: 1,
  storage,
  whitelist: ['checkedColumnKeys', 'sortedColumnKeys'],
};

const caseViewPersistConfig: PersistConfigType<CaseViewState> = {
  key: 'case_view',
  version: 1,
  storage,
  whitelist: ['searchHistories', 'searchPreferences', 'checkedColumnKeys', 'sortedColumnKeys', 'itemsPerPage'],
};

const tagsPersistConfig: PersistConfigType<TagsState> = {
  key: 'tags',
  version: 1,
  storage,
  whitelist: ['itemsPerPage'],
};

const ebaPersistConfig: PersistConfigType<EbaState> = {
  key: 'eba',
  version: 1,
  storage,
  whitelist: ['customFields'],
};

const EBAGroupPersistConfig: PersistConfigType<EbaGroupStateType> = {
  key: 'eba_group',
  version: 1,
  storage: sessionStorage,
  whitelist: ['groupStatus'],
};

const genCombineReducer = (keyPrefix?: string) =>
  combineReducers({
    ...reducer,
    app: persistReducer({ ...appPersistConfig, keyPrefix }, appReducer),
    caseMultiTabs: persistReducer({ ...caseMultiTabsPersistConfig, keyPrefix }, caseMultiTabsReducer),
    caseField: persistReducer({ ...caseFieldPersistConfig, keyPrefix }, caseFieldReducer),
    caseForm: persistReducer({ ...caseFormPersistConfig, keyPrefix }, caseFormReducer),
    caseView: persistReducer({ ...caseViewPersistConfig, keyPrefix }, caseViewReducer),
    tags: persistReducer({ ...tagsPersistConfig, keyPrefix }, tagsReducer),
    eba: persistReducer({ ...ebaPersistConfig, keyPrefix }, ebaReducer),
    ebaGroup: persistReducer({ ...EBAGroupPersistConfig, keyPrefix }, ebaGroupReducer),
  });

const combineReducer = genCombineReducer();

export const store = configureStore({
  reducer: combineReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(MIDDLEWARE_LIST),
  devTools: process.env.NODE_ENV !== 'production',
});

export const persistor = persistStore(store, { manualPersist: true } as PersistorOptions);

export async function swapToUserStore() {
  const me = await AtlasSdk.environment.getUser();
  const keyPrefix = `${me?.id || 'persist'}_`;

  const newCombineReducer = await genCombineReducer(keyPrefix);
  store.replaceReducer(newCombineReducer);
  persistor.persist();
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
